import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ApproveLeaveAdjustmentsDTO,
  CreateLeaveRequestDTO,
  CreateOvertimeRequestDTO,
  LeaveRequestListQueryDTO,
  LineManagerLeaveListQueryDTO,
  OvertimeListQueryDTO,
  UpdateLeaveRequestDTO,
} from "@/modules/hr/domain/contracts";
import { hrApi } from "./client";

export const hrKeys = {
  salaryAdvances: (q: { offset?: number; limit?: number; status?: string }) =>
    ["hr", "salary-advance", "list", q] as const,
  salaryAdvance: (id: string) => ["hr", "salary-advance", id] as const,
  leaveTypes: ["hr", "leave", "types"] as const,
  leaveBalance: (year?: number) => ["hr", "leave", "balance", year] as const,
  leaveRequests: (q: Partial<LeaveRequestListQueryDTO>) =>
    ["hr", "leave", "requests", q] as const,
  leaveRequest: (id: string) => ["hr", "leave", "request", id] as const,
  otRules: ["hr", "overtime", "rules"] as const,
  overtimeRequests: (q: Partial<OvertimeListQueryDTO>) =>
    ["hr", "overtime", "requests", q] as const,
  overtimeRequest: (id: string) => ["hr", "overtime", "request", id] as const,
  payroll: ["hr", "payroll"] as const,
  payrollDetail: (id: string) => ["hr", "payroll", id] as const,
  recentActivity: (limit?: number) =>
    ["hr", "activity", "recent", limit] as const,
  lineManagerLeaveStatus: ["hr", "leave", "line-manager", "status"] as const,
  lineManagerLeaveRequests: (q: Partial<LineManagerLeaveListQueryDTO>) =>
    ["hr", "leave", "line-manager", "requests", q] as const,
  lineManagerLeaveRequest: (id: string) =>
    ["hr", "leave", "line-manager", "request", id] as const,
};

export function useLeaveTypesQuery() {
  return useQuery({
    queryKey: hrKeys.leaveTypes,
    queryFn: () => hrApi.leave.getTypes(),
  });
}

export function useLeaveBalanceQuery(year?: number) {
  return useQuery({
    queryKey: hrKeys.leaveBalance(year),
    queryFn: () => hrApi.leave.getBalance(year),
  });
}

export function useLeaveRequestsQuery(
  query: Partial<LeaveRequestListQueryDTO> = {},
) {
  const q = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    status: query.status,
    sortBy: query.sortBy,
    sortOrder: query.sortOrder,
  };
  return useQuery({
    queryKey: hrKeys.leaveRequests(q),
    queryFn: () => hrApi.leave.list(q),
  });
}

export function useLeaveRequestQuery(id: string) {
  return useQuery({
    queryKey: hrKeys.leaveRequest(id),
    queryFn: () => hrApi.leave.getById(id),
    enabled: !!id,
  });
}

export function useCreateLeaveRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLeaveRequestDTO) => hrApi.leave.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr"] });
    },
    onError: () => toast.error("ບໍ່ສາມາດສົ່ງຄຳຂໍລາໄດ້"),
  });
}

export function useUpdateLeaveRequest(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<UpdateLeaveRequestDTO>) =>
      hrApi.leave.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: hrKeys.leaveRequest(id) });
      qc.invalidateQueries({ queryKey: ["hr"] });
    },
    onError: () => toast.error("ບໍ່ສາມາດແກ້ໄຂຄຳຂໍລາໄດ້"),
  });
}

export function useDeleteLeaveRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => hrApi.leave.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr"] });
    },
    onError: () => toast.error("ບໍ່ສາມາດລຶບຄຳຂໍລາໄດ້"),
  });
}

export function useOtRulesQuery() {
  return useQuery({
    queryKey: hrKeys.otRules,
    queryFn: () => hrApi.overtime.getRules(),
  });
}

export function useOvertimeRequestsQuery(
  query: Partial<OvertimeListQueryDTO> = {},
) {
  const q = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    status: query.status,
    sortBy: query.sortBy,
    sortOrder: query.sortOrder,
  };
  return useQuery({
    queryKey: hrKeys.overtimeRequests(q),
    queryFn: () => hrApi.overtime.list(q),
  });
}

export function useOvertimeRequestQuery(id: string) {
  return useQuery({
    queryKey: hrKeys.overtimeRequest(id),
    queryFn: () => hrApi.overtime.getById(id),
    enabled: !!id,
  });
}

export function useCreateOvertimeRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOvertimeRequestDTO) =>
      hrApi.overtime.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr"] });
    },
    onError: () => toast.error("ບໍ່ສາມາດສົ່ງຄຳຂໍ OT ໄດ້"),
  });
}

export function usePayrollListQuery(options?: {
  year?: number;
  limit?: number;
  offset?: number;
  enabled?: boolean;
}) {
  const limit = options?.limit ?? 10;
  const offset = options?.offset ?? 0;
  const year = options?.year;
  const enabled = options?.enabled ?? true;

  return useQuery({
    queryKey: ["hr", "payroll", "list", year, limit, offset] as const,
    queryFn: () => hrApi.payroll.list(limit, offset, year),
    enabled,
  });
}

export function usePayrollQuery(id: string) {
  return useQuery({
    queryKey: hrKeys.payrollDetail(id),
    queryFn: () => hrApi.payroll.getById(id),
    enabled: !!id,
  });
}

export function useRecentActivityQuery(limit?: number) {
  return useQuery({
    queryKey: hrKeys.recentActivity(limit),
    queryFn: () => hrApi.activity.recent(limit),
  });
}

export function useLineManagerLeaveStatusQuery(enabled = true) {
  return useQuery({
    queryKey: hrKeys.lineManagerLeaveStatus,
    queryFn: () => hrApi.leave.lineManager.status(),
    enabled,
  });
}

export function useLineManagerLeaveRequestsQuery(
  query: Partial<LineManagerLeaveListQueryDTO> = {},
) {
  const q = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    status: query.status,
    sortBy: query.sortBy,
    sortOrder: query.sortOrder,
  };
  return useQuery({
    queryKey: hrKeys.lineManagerLeaveRequests(q),
    queryFn: () => hrApi.leave.lineManager.list(q),
  });
}

export function useLineManagerLeaveRequestQuery(id: string, enabled = true) {
  return useQuery({
    queryKey: hrKeys.lineManagerLeaveRequest(id),
    queryFn: () => hrApi.leave.lineManager.getById(id),
    enabled: Boolean(id) && enabled,
  });
}

export function useApproveLeaveLineMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: ApproveLeaveAdjustmentsDTO & { id: string }) =>
      hrApi.leave.lineManager.approveLine(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr"] });
    },
    onError: () => toast.error("ບໍ່ສາມາດອະນຸມັດຂັ້ນສາຍໄດ້"),
  });
}

export function useRejectLeaveLineMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      rejectionReason,
    }: {
      id: string;
      rejectionReason?: string | null;
    }) => hrApi.leave.lineManager.rejectLine(id, { rejectionReason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr"] });
    },
    onError: () => toast.error("ບໍ່ສາມາດປະຕິເສດຂັ້ນສາຍໄດ້"),
  });
}

// ─── Salary Advance ───

export function useSalaryAdvanceListQuery(q: {
  offset?: number;
  limit?: number;
  status?: string;
}) {
  return useQuery({
    queryKey: hrKeys.salaryAdvances(q),
    queryFn: () => hrApi.salaryAdvance.list(q),
  });
}

export function useSalaryAdvanceDetailQuery(id: string) {
  return useQuery({
    queryKey: hrKeys.salaryAdvance(id),
    queryFn: () => hrApi.salaryAdvance.getById(id),
    enabled: !!id,
  });
}

export function useCreateSalaryAdvanceMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      requestDate: string;
      amount: number;
      reason?: string | null;
    }) => hrApi.salaryAdvance.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr", "salary-advance"] });
    },
  });
}

export function useCancelSalaryAdvanceMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => hrApi.salaryAdvance.cancel(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr", "salary-advance"] });
    },
  });
}
